/**
 * BillingSubscriptions — Admin Subscriptions + Entitlements UI
 *
 * @see US4, US6, AC-14 through AC-17 in docs/specifications/2026-03-08-billing-admin-ui.md
 */
import React, { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import { EmptyState } from "../../../components/common/EmptyState";
import { useBillingAdminBrand } from "../../../hooks/useBillingAdminBrand";
import {
  useBillingSubscriptionsQuery,
  useMigrateSubscriptionMutation,
  useProductsQuery,
} from "../../../hooks/useAdminProductCatalog";
import BillingBrandGuard from "./BillingBrandGuard";
import BillingBrandSelector from "./BillingBrandSelector";
import {
  SubscriptionDetailModal,
  getBillingIntervalLabel,
  SubscriptionRow,
  SubscriptionTableHead,
} from "./components";
import { AdminRenewalPreferenceModal } from "./components/AdminRenewalPreferenceModal";
import type { BillingSubscription } from "../../../types/adminBilling";

const PAGE_LIMIT = 20;

// Re-export so existing imports of getBillingIntervalLabel from BillingSubscriptions
// continue to compile after R1 extraction.
export { getBillingIntervalLabel };


// =============================================================================
// MIGRATE MODAL (US6)
// =============================================================================

interface MigrateModalProps {
  subscriptionId: string;
  brandId: string;
  onClose: () => void;
}

const MigrateModal: React.FC<MigrateModalProps> = ({
  subscriptionId,
  brandId,
  onClose,
}) => {
  const { data: productsData } = useProductsQuery(brandId);
  const activeProducts =
    productsData?.data.filter((p) => p.status === "ACTIVE") ?? [];

  const [selectedProductId, setSelectedProductId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const migrateMutation = useMigrateSubscriptionMutation(brandId);

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const selectedProduct = activeProducts.find(
    (p) => p.id === selectedProductId
  );
  const hasNoFeatures =
    selectedProduct && (selectedProduct.featuresCount ?? 0) === 0;

  const handleMigrate = () => {
    if (!selectedProductId) return;
    setError(null);

    migrateMutation.mutate(
      { subscriptionId, productId: selectedProductId },
      {
        onSuccess: onClose,
        onError: (err: unknown) => {
          const response = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
          const status = response?.status;
          if (status === 409) {
            setError("Subskrypcja została już zmigrowana.");
          } else {
            setError(
              response?.data?.message || "Błąd migracji. Spróbuj ponownie."
            );
          }
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Migracja do nowego systemu
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="migrate-product" className="block text-sm font-medium text-neutral-700 mb-1">
              Produkt docelowy (tylko ACTIVE) *
            </label>
            <select
              id="migrate-product"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Wybierz produkt...</option>
              {activeProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.productType})
                </option>
              ))}
            </select>
            {activeProducts.length === 0 && (
              <p className="text-amber-600 text-xs mt-1">
                Brak aktywnych produktów.
              </p>
            )}
          </div>

          {hasNoFeatures && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-700">
                Wybrany produkt nie ma zdefiniowanych features. Możesz
                kontynuować, ale entitlements będą puste.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium"
          >
            Anuluj
          </button>
          <button
            onClick={handleMigrate}
            disabled={!selectedProductId || migrateMutation.isPending}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {migrateMutation.isPending ? "Migrowanie..." : "Migruj"}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================

const BillingSubscriptions: React.FC = () => {
  const { selectedBrand, brands, isLoading, selectBrand, isBrandRequired } =
    useBillingAdminBrand();

  const [page, setPage] = useState(0);
  const offset = page * PAGE_LIMIT;

  const { data, isLoading: isLoadingSubscriptions, isError: isErrorSubscriptions, refetch: refetchSubscriptions } =
    useBillingSubscriptionsQuery(selectedBrand?.id ?? null, { limit: PAGE_LIMIT, offset });

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);
  const [migratingSubscriptionId, setMigratingSubscriptionId] = useState<
    string | null
  >(null);
  // AC-12: subscription whose next-period package the admin is choosing.
  const [renewalPackageSubscriptionId, setRenewalPackageSubscriptionId] =
    useState<string | null>(null);

  const subscriptions = data?.data ?? [];
  const renewalPackageSubscription =
    subscriptions.find((s) => s.id === renewalPackageSubscriptionId) ?? null;
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  // Reset page when brand changes
  useEffect(() => {
    setPage(0);
  }, [selectedBrand?.id]);

  return (
    <Layout>
      <BillingBrandGuard
        isBrandRequired={isBrandRequired}
        brands={brands}
        isLoading={isLoading}
        onSelectBrand={selectBrand}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Subskrypcje
              </h1>
              <p className="text-neutral-600 mt-1 text-sm">
                Przeglądaj subskrypcje, entitlements i migruj legacy subskrypcje
              </p>
            </div>
            <BillingBrandSelector
              brands={brands}
              selectedBrand={selectedBrand}
              onSelectBrand={selectBrand}
            />
          </div>

          {/* Table */}
          {isErrorSubscriptions ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Wystąpił błąd podczas ładowania danych.</p>
              <button onClick={() => refetchSubscriptions()} className="mt-2 text-primary-700 underline">
                Spróbuj ponownie
              </button>
            </div>
          ) : isLoadingSubscriptions ? (
            <div className="flex items-center justify-center min-h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="bg-white rounded-lg border border-neutral-200">
              <EmptyState
                title="Brak subskrypcji"
                description="Nie znaleziono subskrypcji dla wybranego brandu."
              />
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <SubscriptionTableHead />
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {subscriptions.map((sub: BillingSubscription) => (
                      <SubscriptionRow
                        key={sub.id}
                        subscription={sub}
                        onSelectDetail={setSelectedSubscriptionId}
                        onMigrate={setMigratingSubscriptionId}
                        onChangeRenewalPackage={setRenewalPackageSubscriptionId}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {total > limit && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-gray-600">
                  <span>
                    Wyświetlanie {offset + 1}-{Math.min(offset + limit, total)} z {total} rekordów
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={offset === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Poprzednia
                    </button>
                    <button
                      disabled={offset + limit >= total}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </BillingBrandGuard>

      {/* Detail Modal */}
      {selectedSubscriptionId && selectedBrand && (
        <SubscriptionDetailModal
          subscriptionId={selectedSubscriptionId}
          brandId={selectedBrand.id}
          onClose={() => setSelectedSubscriptionId(null)}
          onMigrate={() => {
            setMigratingSubscriptionId(selectedSubscriptionId);
            setSelectedSubscriptionId(null);
          }}
        />
      )}

      {/* Migrate Modal */}
      {migratingSubscriptionId && selectedBrand && (
        <MigrateModal
          subscriptionId={migratingSubscriptionId}
          brandId={selectedBrand.id}
          onClose={() => setMigratingSubscriptionId(null)}
        />
      )}

      {/* Renewal package modal (AC-12) */}
      {renewalPackageSubscription && selectedBrand && (
        <AdminRenewalPreferenceModal
          subscription={renewalPackageSubscription}
          brandId={selectedBrand.id}
          onClose={() => setRenewalPackageSubscriptionId(null)}
          onSaved={() => refetchSubscriptions()}
        />
      )}
    </Layout>
  );
};

export default BillingSubscriptions;
